import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

/**
 * A customer's review of a field engineer's visit for a single ticket.
 *
 * Stored locally for now; spec doesn't define a SN endpoint for reviews. Once one
 * exists, push from a write hook (afterCreate) without changing the schema.
 */
@Entity()
export class TicketReview extends MongoEntity {
  static override _class = 'TicketReview';
  static override _collectionName = 'smartteams_ticket_reviews';

  /** WOT number (e.g. `WOT0010060`). Unique together with `customerUserId`. */
  @validate(nonOptional())
  @Field(() => String)
  ticketNumber: Maybe<string>;

  /** AdornisUser._id of the customer who submitted this review. */
  @validate(nonOptional())
  @Field(() => String)
  customerUserId: Maybe<string>;

  /** ServiceNow sys_id of the FE being reviewed (denormalised for downstream analytics). */
  @Field(() => String)
  feSysId: Maybe<string>;

  @validate(({ value, key, path }) => {
    if (typeof value !== 'number' || value < 1 || value > 5) {
      throw new ValidationError('Rating must be between 1 and 5', { key, path });
    }
  })
  @Field(() => Int)
  rating: Maybe<number>;

  /** Did the activity finish successfully according to the customer? */
  @validate(nonOptional())
  @Field(() => Boolean)
  successfullyCompleted: Maybe<boolean>;

  @Field(() => String)
  comment: Maybe<string>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;
}
